/**
 * ConnectionSetupCard Component
 *
 * Shown after the assistant applies a workflow to canvas when some actions
 * need connections that couldn't be auto-wired. Lists unresolved items
 * with a "Set up" button that navigates to the action's Connection tab.
 * Reacts to live workflow/connection changes — items update as the user
 * sets up connections or removes operations.
 */

import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import {
  PlugConnectedRegular,
  WarningRegular,
  ArrowRightRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';
import type { ConnectionMatchingResult } from '@/types/connectionProposal';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useTelemetry } from '@/hooks/useTelemetry';
import { getActionReferenceName } from '@/utils/connections';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteMarigoldBorderActive}`,
    backgroundColor: tokens.colorStatusWarningBackground1,
    overflow: 'hidden',
    marginTop: tokens.spacingVerticalXS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorPaletteMarigoldBorderActive}`,
  },
  headerIcon: {
    color: tokens.colorPaletteMarigoldForeground2,
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalXXS,
  },
  resolvedItem: {
    opacity: 0.6,
  },
  warningIcon: {
    color: tokens.colorPaletteMarigoldForeground2,
    fontSize: tokens.fontSizeBase300,
    flexShrink: 0,
  },
  connectedIcon: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: tokens.fontSizeBase300,
    flexShrink: 0,
  },
  removedIcon: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase300,
    flexShrink: 0,
  },
  actionName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removedName: {
    textDecorationLine: 'line-through',
    color: tokens.colorNeutralForeground4,
  },
  connectorHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  setupButton: {
    flexShrink: 0,
  },
});

type ItemStatus = 'needs-setup' | 'connected' | 'removed';

/** Walk the action tree (including Switch/ForEach/If-Else/Until scopes) to find an action by name. */
function findAction(name: string, actions: Record<string, unknown> | undefined): unknown | undefined {
  if (!actions) return undefined;
  if (actions[name]) return actions[name];
  for (const action of Object.values(actions)) {
    if (!action || typeof action !== 'object') continue;
    const a = action as Record<string, unknown>;
    // Nested .actions (ForEach, Until, If-Else true branch)
    const found = findAction(name, a.actions as Record<string, unknown> | undefined);
    if (found) return found;
    // If-Else false branch
    if (a.else && typeof a.else === 'object') {
      const elseFound = findAction(name, (a.else as Record<string, unknown>).actions as Record<string, unknown> | undefined);
      if (elseFound) return elseFound;
    }
    // Switch cases
    if (a.cases && typeof a.cases === 'object') {
      for (const c of Object.values(a.cases as Record<string, unknown>)) {
        if (c && typeof c === 'object') {
          const caseFound = findAction(name, (c as Record<string, unknown>).actions as Record<string, unknown> | undefined);
          if (caseFound) return caseFound;
        }
      }
      if (a.default && typeof a.default === 'object') {
        const defFound = findAction(name, (a.default as Record<string, unknown>).actions as Record<string, unknown> | undefined);
        if (defFound) return defFound;
      }
    }
  }
  return undefined;
}

interface ConnectionSetupCardProps {
  connectionSetup: ConnectionMatchingResult;
}

export const ConnectionSetupCard = ({ connectionSetup }: ConnectionSetupCardProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const selectNodeByName = useWorkflowStore(s => s.selectNodeByName);
  const workflow = useWorkflowStore(s => s.workflow);
  const references = useConnectionStore(s => s.references);
  const setInitialTab = useNodeEditorStore(s => s.setInitialTab);

  // Build a set of trigger names from the live workflow
  const triggerNames = useMemo(() => {
    const triggers = workflow?.definition?.triggers;
    return triggers ? new Set(Object.keys(triggers)) : new Set<string>();
  }, [workflow?.definition?.triggers]);

  // Collect unresolved items with live status
  const items = useMemo(() => {
    const allItems = [
      ...connectionSetup.noConnectionAvailable.map(req => ({
        actionName: req.actionName,
        connectorDisplayName: req.connectorDisplayName,
      })),
      ...connectionSetup.needsApproval
        .filter(p => p.status === 'pending')
        .map(p => ({
          actionName: p.requirement.actionName,
          connectorDisplayName: p.requirement.connectorDisplayName,
        })),
    ];

    // Check live status of each item against the current workflow
    return allItems.map(item => {
      const isTrigger = triggerNames.has(item.actionName);
      const operation = isTrigger
        ? (workflow?.definition?.triggers as Record<string, unknown> | undefined)?.[item.actionName]
        : findAction(item.actionName, workflow?.definition?.actions as Record<string, unknown> | undefined);

      let status: ItemStatus = 'needs-setup';
      if (!operation) {
        status = 'removed';
      } else {
        const refName = getActionReferenceName(operation as { inputs?: unknown });
        if (refName && Object.keys(references).some(k => k.toLowerCase() === refName.toLowerCase())) {
          status = 'connected';
        }
      }

      return { ...item, isTrigger, status };
    });
  }, [connectionSetup, triggerNames, workflow, references]);

  const handleSetup = useCallback((actionName: string, isTrigger: boolean) => {
    trackEvent('agent.chat.connection_setup.navigate', { actionName });
    setInitialTab('connection');
    selectNodeByName(actionName, isTrigger ? 'trigger' : 'action');
  }, [selectNodeByName, setInitialTab, trackEvent]);

  if (items.length === 0) return null;

  const pendingCount = items.filter(i => i.status === 'needs-setup').length;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <PlugConnectedRegular className={styles.headerIcon} />
        <Text className={styles.headerTitle} weight="semibold">
          {pendingCount === 0
            ? 'All connections resolved'
            : pendingCount === 1
              ? '1 operation needs a connection'
              : `${pendingCount} operations need connections`}
        </Text>
      </div>
      <div className={styles.body}>
        {items.map(item => (
          <div
            key={item.actionName}
            className={mergeClasses(styles.item, item.status !== 'needs-setup' && styles.resolvedItem)}
          >
            {item.status === 'connected' && <CheckmarkCircleRegular className={styles.connectedIcon} />}
            {item.status === 'removed' && <DismissCircleRegular className={styles.removedIcon} />}
            {item.status === 'needs-setup' && <WarningRegular className={styles.warningIcon} />}
            <Text
              className={mergeClasses(styles.actionName, item.status === 'removed' && styles.removedName)}
              title={item.actionName}
            >
              {item.actionName}
            </Text>
            <Text className={styles.connectorHint}>
              {item.connectorDisplayName}
            </Text>
            {item.status === 'needs-setup' && (
              <Button
                className={styles.setupButton}
                appearance="outline"
                size="small"
                icon={<ArrowRightRegular />}
                iconPosition="after"
                aria-label={`Set up connection for ${item.actionName}`}
                onClick={() => handleSetup(item.actionName, item.isTrigger)}
              >
                Set up
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
